import { useState, useEffect, useMemo, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Mail, 
  Users, 
  List, 
  Send, 
  Plus, 
  Trash2, 
  BarChart3, 
  Layers,
  Calendar,
  Edit,
  Play,
  Pause,
  CheckCircle2,
  Copy,
  Home,
  Image as ImageIcon,
  Link as LinkIcon
} from "lucide-react";
import LexicalEditor from '@/components/LexicalEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Pour les liens publics (inscription), utiliser le domaine actuel
const PUBLIC_DOMAIN = window.location.origin;

// ============= USING LEXICAL RICH TEXT EDITOR =============
// The RichTextEditor component is now imported from /components/LexicalEditor.js
const RichTextEditor = LexicalEditor;

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_lists: 0,
    total_subscribers: 0,
    total_campaigns: 0,
    total_sent: 0,
    active_sequences: 0
  });
  const [lists, setLists] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListData, setNewListData] = useState({ name: "", business_type: "", description: "" });

  useEffect(() => {
    loadStats();
    loadLists();
    loadCampaigns();
  }, []);

  const loadStats = async () => {
    try {
      // Ajouter timestamp pour éviter le cache
      const response = await axios.get(`${API}/stats?t=${Date.now()}`);
      console.log("📊 Stats API Response:", response.data);
      console.log("📊 Total subscribers from API:", response.data.total_subscribers);
      setStats({
        total_lists: response.data.total_lists || 0,
        total_subscribers: response.data.total_subscribers || 0,
        total_campaigns: response.data.total_campaigns || 0,
        total_sent: response.data.total_sent || 0,
        active_sequences: response.data.active_sequences || 0
      });
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const loadLists = async () => {
    try {
      const response = await axios.get(`${API}/lists`);
      setLists(response.data);
    } catch (error) {
      console.error("Error loading lists:", error);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data.slice(0, 5));
    } catch (error) {
      console.error("Error loading campaigns:", error);
    }
  };

  const createList = async () => {
    try {
      await axios.post(`${API}/lists`, newListData);
      toast.success("Liste créée avec succès!");
      setNewListOpen(false);
      setNewListData({ name: "", business_type: "", description: "" });
      loadLists();
      loadStats();
    } catch (error) {
      toast.error("Erreur lors de la création de la liste");
    }
  };

  const deleteList = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette liste?")) return;
    try {
      await axios.delete(`${API}/lists/${id}`);
      toast.success("Liste supprimée");
      loadLists();
      loadStats();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Autorépondeur
              </h1>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/")} 
                data-testid="nav-dashboard"
                className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate("/lists")} 
                data-testid="nav-lists"
                className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50"
              >
                <List className="w-4 h-4 mr-2" />
                Listes
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate("/campaigns")} 
                data-testid="nav-campaigns"
                className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50"
              >
                <Send className="w-4 h-4 mr-2" />
                Campagnes
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate("/sequences")} 
                data-testid="nav-sequences"
                className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50"
              >
                <Layers className="w-4 h-4 mr-2" />
                Séquences
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate("/newsletters")} 
                data-testid="nav-newsletters"
                className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50"
              >
                <Mail className="w-4 h-4 mr-2" />
                Newsletter
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card data-testid="stat-lists" className="bg-white/60 backdrop-blur border-slate-200/50 hover:shadow-lg transition-all duration-300">
            <CardHeader className="pb-3">
              <CardDescription className="text-slate-600">Listes</CardDescription>
              <CardTitle className="text-3xl font-bold text-cyan-600">{stats.total_lists}</CardTitle>
            </CardHeader>
          </Card>
          
          <Card data-testid="stat-subscribers" className="bg-white/60 backdrop-blur border-slate-200/50 hover:shadow-lg transition-all duration-300">
            <CardHeader className="pb-3">
              <CardDescription className="text-slate-600">Abonnés</CardDescription>
              <CardTitle className="text-3xl font-bold text-blue-600">{stats.total_subscribers}</CardTitle>
            </CardHeader>
          </Card>
          
          <Card data-testid="stat-campaigns" className="bg-white/60 backdrop-blur border-slate-200/50 hover:shadow-lg transition-all duration-300">
            <CardHeader className="pb-3">
              <CardDescription className="text-slate-600">Campagnes</CardDescription>
              <CardTitle className="text-3xl font-bold text-purple-600">{stats.total_campaigns}</CardTitle>
            </CardHeader>
          </Card>
          
          <Card data-testid="stat-sent" className="bg-white/60 backdrop-blur border-slate-200/50 hover:shadow-lg transition-all duration-300">
            <CardHeader className="pb-3">
              <CardDescription className="text-slate-600">Emails envoyés</CardDescription>
              <CardTitle className="text-3xl font-bold text-emerald-600">{stats.total_sent}</CardTitle>
            </CardHeader>
          </Card>
          
          <Card data-testid="stat-sequences" className="bg-white/60 backdrop-blur border-slate-200/50 hover:shadow-lg transition-all duration-300">
            <CardHeader className="pb-3">
              <CardDescription className="text-slate-600">Séquences actives</CardDescription>
              <CardTitle className="text-3xl font-bold text-orange-600">{stats.active_sequences}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-white/60 backdrop-blur border-slate-200/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-slate-800">Listes de distribution</CardTitle>
                <Dialog open={newListOpen} onOpenChange={setNewListOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="create-list-btn" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Nouvelle liste
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Créer une nouvelle liste</DialogTitle>
                      <DialogDescription>
                        Organisez vos contacts par business ou thématique
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Nom de la liste</Label>
                        <Input
                          data-testid="list-name-input"
                          value={newListData.name}
                          onChange={(e) => setNewListData({...newListData, name: e.target.value})}
                          placeholder="Newsletter principal"
                        />
                      </div>
                      <div>
                        <Label>Type de business</Label>
                        <Input
                          data-testid="list-business-input"
                          value={newListData.business_type}
                          onChange={(e) => setNewListData({...newListData, business_type: e.target.value})}
                          placeholder="E-commerce, Formation, etc."
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          data-testid="list-description-input"
                          value={newListData.description}
                          onChange={(e) => setNewListData({...newListData, description: e.target.value})}
                          placeholder="Description de la liste"
                        />
                      </div>
                      <Button 
                        onClick={createList} 
                        data-testid="submit-list-btn"
                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-600"
                      >
                        Créer la liste
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {lists.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Aucune liste créée</p>
                ) : (
                  lists.slice(0, 5).map(list => (
                    <div key={list.id} className="flex items-center justify-between p-3 bg-slate-50/50 rounded-lg hover:bg-slate-100/50 transition-colors" data-testid={`list-item-${list.id}`}>
                      <div>
                        <p className="font-medium text-slate-800">{list.name}</p>
                        <p className="text-sm text-slate-500">{list.business_type} • {list.subscriber_count} abonnés</p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => navigate(`/lists/${list.id}`)}
                          data-testid={`view-list-${list.id}`}
                          className="hover:text-cyan-600 hover:bg-cyan-50"
                        >
                          <Users className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => deleteList(list.id)}
                          data-testid={`delete-list-${list.id}`}
                          className="hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/60 backdrop-blur border-slate-200/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-slate-800">Campagnes récentes</CardTitle>
                <Button 
                  size="sm" 
                  onClick={() => navigate("/campaigns")} 
                  data-testid="view-all-campaigns-btn"
                  className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Nouvelle campagne
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {campaigns.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Aucune campagne créée</p>
                ) : (
                  campaigns.map(campaign => (
                    <div key={campaign.id} className="flex items-center justify-between p-3 bg-slate-50/50 rounded-lg" data-testid={`campaign-item-${campaign.id}`}>
                      <div>
                        <p className="font-medium text-slate-800">{campaign.name}</p>
                        <p className="text-sm text-slate-500">{campaign.sent_count} envoyés</p>
                      </div>
                      <Badge 
                        variant={campaign.status === 'sent' ? 'default' : 'secondary'}
                        className={campaign.status === 'sent' ? 'bg-emerald-500' : ''}
                      >
                        {campaign.status}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const ListsPage = () => {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListData, setNewListData] = useState({ name: "", business_type: "", description: "" });

  useEffect(() => {
    loadLists();
  }, []);

  const loadLists = async () => {
    try {
      const response = await axios.get(`${API}/lists`);
      setLists(response.data);
    } catch (error) {
      console.error("Error loading lists:", error);
    }
  };

  const createList = async () => {
    try {
      await axios.post(`${API}/lists`, newListData);
      toast.success("Liste créée avec succès!");
      setNewListOpen(false);
      setNewListData({ name: "", business_type: "", description: "" });
      loadLists();
    } catch (error) {
      toast.error("Erreur lors de la création de la liste");
    }
  };

  const deleteList = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette liste?")) return;
    try {
      await axios.delete(`${API}/lists/${id}`);
      toast.success("Liste supprimée");
      loadLists();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Autorépondeur
              </h1>
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50">
                <BarChart3 className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <Button variant="ghost" onClick={() => navigate("/lists")} className="text-cyan-600 bg-cyan-50">
                <List className="w-4 h-4 mr-2" />
                Listes
              </Button>
              <Button variant="ghost" onClick={() => navigate("/campaigns")} className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50">
                <Send className="w-4 h-4 mr-2" />
                Campagnes
              </Button>
              <Button variant="ghost" onClick={() => navigate("/sequences")} className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50">
                <Layers className="w-4 h-4 mr-2" />
                Séquences
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold text-slate-800">Listes de distribution</h2>
          <Dialog open={newListOpen} onOpenChange={setNewListOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-new-list-btn" className="bg-gradient-to-r from-cyan-500 to-blue-600">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle liste
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Créer une nouvelle liste</DialogTitle>
                <DialogDescription>
                  Organisez vos contacts par business ou thématique
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Nom de la liste</Label>
                  <Input
                    value={newListData.name}
                    onChange={(e) => setNewListData({...newListData, name: e.target.value})}
                    placeholder="Newsletter principal"
                  />
                </div>
                <div>
                  <Label>Type de business</Label>
                  <Input
                    value={newListData.business_type}
                    onChange={(e) => setNewListData({...newListData, business_type: e.target.value})}
                    placeholder="E-commerce, Formation, etc."
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={newListData.description}
                    onChange={(e) => setNewListData({...newListData, description: e.target.value})}
                    placeholder="Description de la liste"
                  />
                </div>
                <Button onClick={createList} className="w-full bg-gradient-to-r from-cyan-500 to-blue-600">
                  Créer la liste
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {lists.map(list => (
            <Card key={list.id} className="bg-white/60 backdrop-blur border-slate-200/50 hover:shadow-lg transition-all duration-300" data-testid={`list-card-${list.id}`}>
              <CardHeader>
                <CardTitle className="text-slate-800">{list.name}</CardTitle>
                <CardDescription>{list.business_type}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">{list.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Users className="w-4 h-4" />
                    <span className="text-sm font-medium">{list.subscriber_count} abonnés</span>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => navigate(`/lists/${list.id}`)}
                      className="hover:text-cyan-600 hover:bg-cyan-50"
                    >
                      <Users className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => deleteList(list.id)}
                      className="hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

const ListDetailPage = () => {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [addSubscriberOpen, setAddSubscriberOpen] = useState(false);
  const [newSubscriber, setNewSubscriber] = useState({ email: "", first_name: "", last_name: "" });

  useEffect(() => {
    loadList();
    loadSubscribers();
  }, [listId]);

  const loadList = async () => {
    try {
      const response = await axios.get(`${API}/lists/${listId}`);
      setList(response.data);
    } catch (error) {
      console.error("Error loading list:", error);
    }
  };

  const loadSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/subscribers?list_id=${listId}`);
      setSubscribers(response.data);
    } catch (error) {
      console.error("Error loading subscribers:", error);
    }
  };

  const addSubscriber = async () => {
    try {
      await axios.post(`${API}/subscribers`, { ...newSubscriber, list_id: listId });
      toast.success("Abonné ajouté! Email de vérification envoyé.");
      setAddSubscriberOpen(false);
      setNewSubscriber({ email: "", first_name: "", last_name: "" });
      loadSubscribers();
      loadList();
    } catch (error) {
      toast.error("Erreur lors de l'ajout de l'abonné");
    }
  };

  const [editSubscriberOpen, setEditSubscriberOpen] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);

  const openEditSubscriber = (subscriber) => {
    setEditingSubscriber({
      id: subscriber.id,
      email: subscriber.email,
      first_name: subscriber.first_name || "",
      last_name: subscriber.last_name || ""
    });
    setEditSubscriberOpen(true);
  };

  const updateSubscriber = async () => {
    try {
      await axios.put(`${API}/subscribers/${editingSubscriber.id}`, {
        email: editingSubscriber.email,
        first_name: editingSubscriber.first_name,
        last_name: editingSubscriber.last_name
      });
      toast.success("Abonné modifié!");
      setEditSubscriberOpen(false);
      setEditingSubscriber(null);
      loadSubscribers();
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  const verifySubscriberManual = async (subscriberId) => {
    try {
      await axios.post(`${API}/subscribers/${subscriberId}/verify-manual`);
      toast.success("Abonné vérifié!");
      loadSubscribers();
      loadList();
    } catch (error) {
      toast.error("Erreur lors de la vérification");
    }
  };

  const deleteSubscriber = async (subscriberId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet abonné?")) return;
    try {
      await axios.delete(`${API}/subscribers/${subscriberId}`);
      toast.success("Abonné supprimé");
      loadSubscribers();
      loadList();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (!list) return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate("/lists")} className="hover:bg-cyan-50">
                ← Retour
              </Button>
              <h1 className="text-2xl font-bold text-slate-800">{list.name}</h1>
            </div>
            <Dialog open={addSubscriberOpen} onOpenChange={setAddSubscriberOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-subscriber-btn" className="bg-gradient-to-r from-cyan-500 to-blue-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un abonné
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ajouter un abonné</DialogTitle>
                  <DialogDescription>
                    Un email de vérification sera envoyé automatiquement
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      data-testid="subscriber-email-input"
                      value={newSubscriber.email}
                      onChange={(e) => setNewSubscriber({...newSubscriber, email: e.target.value})}
                      placeholder="contact@example.com"
                    />
                  </div>
                  <div>
                    <Label>Prénom</Label>
                    <Input
                      value={newSubscriber.first_name}
                      onChange={(e) => setNewSubscriber({...newSubscriber, first_name: e.target.value})}
                      placeholder="Jean"
                    />
                  </div>
                  <div>
                    <Label>Nom</Label>
                    <Input
                      value={newSubscriber.last_name}
                      onChange={(e) => setNewSubscriber({...newSubscriber, last_name: e.target.value})}
                      placeholder="Dupont"
                    />
                  </div>
                  <Button onClick={addSubscriber} data-testid="submit-subscriber-btn" className="w-full bg-gradient-to-r from-cyan-500 to-blue-600">
                    Ajouter
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Modal Modifier Abonné */}
            <Dialog open={editSubscriberOpen} onOpenChange={setEditSubscriberOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Modifier l'abonné</DialogTitle>
                  <DialogDescription>
                    Modifiez les informations de l'abonné
                  </DialogDescription>
                </DialogHeader>
                {editingSubscriber && (
                  <div className="space-y-4">
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        data-testid="edit-subscriber-email-input"
                        value={editingSubscriber.email}
                        onChange={(e) => setEditingSubscriber({...editingSubscriber, email: e.target.value})}
                        placeholder="contact@example.com"
                      />
                    </div>
                    <div>
                      <Label>Prénom</Label>
                      <Input
                        data-testid="edit-subscriber-firstname-input"
                        value={editingSubscriber.first_name}
                        onChange={(e) => setEditingSubscriber({...editingSubscriber, first_name: e.target.value})}
                        placeholder="Jean"
                      />
                    </div>
                    <div>
                      <Label>Nom</Label>
                      <Input
                        data-testid="edit-subscriber-lastname-input"
                        value={editingSubscriber.last_name}
                        onChange={(e) => setEditingSubscriber({...editingSubscriber, last_name: e.target.value})}
                        placeholder="Dupont"
                      />
                    </div>
                    <Button onClick={updateSubscriber} data-testid="submit-edit-subscriber-btn" className="w-full bg-gradient-to-r from-cyan-500 to-blue-600">
                      Enregistrer les modifications
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="bg-white/60 backdrop-blur border-slate-200/50 mb-6">
          <CardHeader>
            <CardTitle>Informations de la liste</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-600">Type de business</p>
                <p className="font-medium text-slate-800">{list.business_type}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Nombre d'abonnés</p>
                <p className="font-medium text-slate-800">{list.subscriber_count}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-slate-600">Description</p>
                <p className="text-slate-800">{list.description || "Aucune description"}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-slate-600 mb-2">Formulaire d'inscription public</p>
                <div className="flex gap-2">
                  <Input 
                    value={`${PUBLIC_DOMAIN}/subscribe/${listId}`}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      const link = `${PUBLIC_DOMAIN}/subscribe/${listId}`;
                      try {
                        navigator.clipboard.writeText(link);
                        toast.success("Lien copié!");
                      } catch (err) {
                        // Fallback: créer un élément temporaire pour copier
                        const textArea = document.createElement("textarea");
                        textArea.value = link;
                        textArea.style.position = "fixed";
                        textArea.style.opacity = "0";
                        document.body.appendChild(textArea);
                        textArea.select();
                        try {
                          document.execCommand('copy');
                          toast.success("Lien copié!");
                        } catch (e) {
                          toast.error("Impossible de copier. Copiez manuellement: " + link);
                        }
                        document.body.removeChild(textArea);
                      }
                    }}
                    data-testid="copy-subscribe-link"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Partagez ce lien pour que les gens s'inscrivent automatiquement</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/60 backdrop-blur border-slate-200/50">
          <CardHeader>
            <CardTitle>Abonnés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {subscribers.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Aucun abonné</p>
              ) : (
                subscribers.map(sub => (
                  <div key={sub.id} className="flex items-center justify-between p-3 bg-slate-50/50 rounded-lg" data-testid={`subscriber-${sub.id}`}>
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium text-slate-800">
                          {sub.first_name && sub.last_name ? `${sub.first_name} ${sub.last_name}` : sub.email}
                        </p>
                        {sub.first_name && sub.last_name && (
                          <p className="text-sm text-slate-500">{sub.email}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {sub.verified ? (
                        <Badge className="bg-emerald-500">Vérifié</Badge>
                      ) : (
                        <>
                          <Badge variant="secondary">En attente</Badge>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => verifySubscriberManual(sub.id)}
                            data-testid={`verify-subscriber-${sub.id}`}
                            className="h-7 text-xs border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Vérifier
                          </Button>
                        </>
                      )}
                      {sub.is_active ? (
                        <Badge className="bg-blue-500">Actif</Badge>
                      ) : (
                        <Badge variant="outline">Inactif</Badge>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => openEditSubscriber(sub)}
                        data-testid={`edit-subscriber-${sub.id}`}
                        className="hover:text-cyan-600 hover:bg-cyan-50"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => deleteSubscriber(sub.id)}
                        data-testid={`delete-subscriber-${sub.id}`}
                        className="hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const CampaignsPage = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [newCampaignOpen, setNewCampaignOpen] = useState(false);
  const [editCampaignOpen, setEditCampaignOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resendingCampaign, setResendingCampaign] = useState(null);
  const [selectedEmailIndexes, setSelectedEmailIndexes] = useState([]);
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    emails: [{ subject: "", content: "", html_content: "" }],
    list_ids: [],
    scheduled_time: null
  });

  useEffect(() => {
    loadCampaigns();
    loadLists();
    loadTemplates();
  }, []);

  const loadCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error("Error loading campaigns:", error);
    }
  };

  const loadLists = async () => {
    try {
      const response = await axios.get(`${API}/lists`);
      setLists(response.data);
    } catch (error) {
      console.error("Error loading lists:", error);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Error loading templates:", error);
    }
  };

  const createCampaign = async () => {
    // Validation
    if (!newCampaign.name || newCampaign.list_ids.length === 0) {
      toast.error("Nom et listes requis");
      return;
    }
    const validEmails = newCampaign.emails.filter(e => e.subject && e.content);
    if (validEmails.length === 0) {
      toast.error("Au moins un email avec sujet et contenu requis");
      return;
    }
    
    try {
      await axios.post(`${API}/campaigns`, { ...newCampaign, emails: validEmails });
      toast.success("Campagne créée!");
      setNewCampaignOpen(false);
      setNewCampaign({ name: "", emails: [{ subject: "", content: "", html_content: "" }], list_ids: [], scheduled_time: null });
      loadCampaigns();
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const openResendModal = (campaign) => {
    setResendingCampaign(campaign);
    setSelectedEmailIndexes(campaign.emails.map((_, idx) => idx)); // Tous sélectionnés par défaut
    setResendModalOpen(true);
  };

  const toggleEmailSelection = (index) => {
    setSelectedEmailIndexes(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const sendCampaign = async (campaignId, isResend = false) => {
    const message = isResend ? "Renvoyer cette campagne?" : "Envoyer cette campagne maintenant?";
    if (!window.confirm(message)) return;
    try {
      await axios.post(`${API}/campaigns/${campaignId}/send`);
      toast.success(isResend ? "Campagne en cours de renvoi!" : "Campagne en cours d'envoi!");
      loadCampaigns();
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    }
  };

  const resendSelectedEmails = async () => {
    if (selectedEmailIndexes.length === 0) {
      toast.error("Sélectionnez au moins un email");
      return;
    }
    try {
      await axios.post(`${API}/campaigns/${resendingCampaign.id}/send-selected`, {
        email_indexes: selectedEmailIndexes
      });
      toast.success(`${selectedEmailIndexes.length} email(s) en cours d'envoi!`);
      setResendModalOpen(false);
      loadCampaigns();
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette campagne?")) return;
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`);
      toast.success("Campagne supprimée");
      loadCampaigns();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const addEmail = () => {
    setNewCampaign({
      ...newCampaign,
      emails: [...newCampaign.emails, { subject: "", content: "", html_content: "" }]
    });
  };

  const updateEmail = (index, field, value) => {
    const updated = [...newCampaign.emails];
    updated[index][field] = value;
    setNewCampaign({ ...newCampaign, emails: updated });
  };

  const removeEmail = (index, isEdit = false) => {
    if (isEdit && editingCampaign) {
      if (editingCampaign.emails.length === 1) {
        toast.error("Au moins un email requis");
        return;
      }
      const updated = editingCampaign.emails.filter((_, i) => i !== index);
      setEditingCampaign({ ...editingCampaign, emails: updated });
    } else {
      if (newCampaign.emails.length === 1) {
        toast.error("Au moins un email requis");
        return;
      }
      const updated = newCampaign.emails.filter((_, i) => i !== index);
      setNewCampaign({ ...newCampaign, emails: updated });
    }
  };

  const openEditCampaign = async (campaignId) => {
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}`);
      setEditingCampaign(response.data);
      setEditCampaignOpen(true);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
  };

  const updateCampaign = async () => {
    const validEmails = editingCampaign.emails.filter(e => e.subject && e.content);
    if (!editingCampaign.name || editingCampaign.list_ids.length === 0 || validEmails.length === 0) {
      toast.error("Nom, listes et au moins un email requis");
      return;
    }
    
    try {
      await axios.put(`${API}/campaigns/${editingCampaign.id}`, {
        name: editingCampaign.name,
        emails: validEmails,
        list_ids: editingCampaign.list_ids,
        scheduled_time: editingCampaign.scheduled_time
      });
      toast.success("Campagne modifiée!");
      setEditCampaignOpen(false);
      setEditingCampaign(null);
      loadCampaigns();
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  const addEmailToEdit = () => {
    if (editingCampaign) {
      setEditingCampaign({
        ...editingCampaign,
        emails: [...editingCampaign.emails, { subject: "", content: "", html_content: "" }]
      });
    }
  };

  const updateEmailInEdit = (index, field, value) => {
    if (editingCampaign) {
      const updated = [...editingCampaign.emails];
      updated[index][field] = value;
      setEditingCampaign({ ...editingCampaign, emails: updated });
    }
  };

  const toggleListSelection = (listId, isEdit = false) => {
    if (isEdit && editingCampaign) {
      setEditingCampaign(prev => ({
        ...prev,
        list_ids: prev.list_ids.includes(listId)
          ? prev.list_ids.filter(id => id !== listId)
          : [...prev.list_ids, listId]
      }));
    } else {
      setNewCampaign(prev => ({
        ...prev,
        list_ids: prev.list_ids.includes(listId)
          ? prev.list_ids.filter(id => id !== listId)
          : [...prev.list_ids, listId]
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      {/* Modal de sélection d'emails pour renvoi */}
      <Dialog open={resendModalOpen} onOpenChange={setResendModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Sélectionnez les emails à renvoyer</DialogTitle>
            <DialogDescription>
              {resendingCampaign?.name} - Choisissez les emails que vous souhaitez renvoyer
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {resendingCampaign?.emails.map((email, idx) => (
              <label 
                key={idx}
                htmlFor={`email-select-${idx}`}
                className={`p-4 border rounded-lg cursor-pointer transition-all block ${
                  selectedEmailIndexes.includes(idx) 
                    ? 'border-cyan-500 bg-cyan-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input 
                    type="checkbox"
                    id={`email-select-${idx}`}
                    checked={selectedEmailIndexes.includes(idx)}
                    onChange={() => toggleEmailSelection(idx)}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-slate-800">{email.subject}</div>
                    <div className="text-sm text-slate-500 mt-1 line-clamp-2">{email.content}</div>
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div className="flex justify-between items-center pt-4 border-t">
            <span className="text-sm text-slate-600">
              {selectedEmailIndexes.length} email(s) sélectionné(s)
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setResendModalOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={resendSelectedEmails}
                disabled={selectedEmailIndexes.length === 0}
                className="bg-gradient-to-r from-cyan-500 to-blue-600"
              >
                <Send className="w-4 h-4 mr-2" />
                Envoyer {selectedEmailIndexes.length} email(s)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Autorépondeur
              </h1>
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50">
                <BarChart3 className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <Button variant="ghost" onClick={() => navigate("/lists")} className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50">
                <List className="w-4 h-4 mr-2" />
                Listes
              </Button>
              <Button variant="ghost" onClick={() => navigate("/campaigns")} className="text-cyan-600 bg-cyan-50">
                <Send className="w-4 h-4 mr-2" />
                Campagnes
              </Button>
              <Button variant="ghost" onClick={() => navigate("/sequences")} className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50">
                <Layers className="w-4 h-4 mr-2" />
                Séquences
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold text-slate-800">Campagnes email</h2>
          <Dialog open={newCampaignOpen} onOpenChange={setNewCampaignOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-campaign-btn" className="bg-gradient-to-r from-purple-500 to-pink-600">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle campagne
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Créer une campagne</DialogTitle>
                <DialogDescription>
                  Rédigez et envoyez votre email à vos listes
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Nom de la campagne</Label>
                  <Input
                    data-testid="campaign-name-input"
                    value={newCampaign.name}
                    onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                    placeholder="Newsletter Janvier 2025"
                  />
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Emails de la campagne</Label>
                    <Button size="sm" variant="outline" onClick={addEmail} data-testid="add-campaign-email-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter un email
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {newCampaign.emails.map((email, index) => (
                      <Card key={index} className="bg-slate-50/50">
                        <CardContent className="pt-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-bold">Email {index + 1}</Label>
                            {newCampaign.emails.length > 1 && (
                              <Button size="sm" variant="ghost" onClick={() => removeEmail(index)} className="h-6 text-red-600">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                          <div>
                            <Label className="text-xs">Objet</Label>
                            <Input
                              value={email.subject}
                              onChange={(e) => updateEmail(index, 'subject', e.target.value)}
                              placeholder="Sujet de l'email"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Contenu 📝</Label>
                            <RichTextEditor
                              value={email.content}
                              onChange={(value) => updateEmail(index, 'content', value)}
                              placeholder="Créez un email attractif avec du texte formaté, des images et des liens..."
                            />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>Listes de destination</Label>
                  <div className="mt-2 space-y-2">
                    {lists.map(list => (
                      <div key={list.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`list-${list.id}`}
                          checked={newCampaign.list_ids.includes(list.id)}
                          onChange={() => toggleListSelection(list.id)}
                          className="w-4 h-4"
                        />
                        <label htmlFor={`list-${list.id}`} className="text-sm text-slate-700">
                          {list.name} ({list.subscriber_count} abonnés)
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <Button onClick={createCampaign} data-testid="submit-campaign-btn" className="w-full bg-gradient-to-r from-purple-500 to-pink-600">
                  Créer la campagne
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Modal Modifier Campagne */}
          <Dialog open={editCampaignOpen} onOpenChange={setEditCampaignOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Modifier la campagne</DialogTitle>
                <DialogDescription>
                  Modifiez votre campagne email
                </DialogDescription>
              </DialogHeader>
              {editingCampaign && (
                <div className="space-y-4">
                  <div>
                    <Label>Nom de la campagne</Label>
                    <Input
                      value={editingCampaign.name}
                      onChange={(e) => setEditingCampaign({...editingCampaign, name: e.target.value})}
                      placeholder="Newsletter Janvier 2025"
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Emails de la campagne</Label>
                      <Button size="sm" variant="outline" onClick={addEmailToEdit}>
                        <Plus className="w-4 h-4 mr-1" />
                        Ajouter un email
                      </Button>
                    </div>
                    <div className="space-y-4">
                      {editingCampaign.emails.map((email, index) => (
                        <Card key={index} className="bg-slate-50/50">
                          <CardContent className="pt-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs font-bold">Email {index + 1}</Label>
                              {editingCampaign.emails.length > 1 && (
                                <Button size="sm" variant="ghost" onClick={() => removeEmail(index, true)} className="h-6 text-red-600">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                            <div>
                              <Label className="text-xs">Objet</Label>
                              <Input
                                value={email.subject}
                                onChange={(e) => updateEmailInEdit(index, 'subject', e.target.value)}
                                placeholder="Sujet de l'email"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Contenu 📝</Label>
                              <RichTextEditor
                                value={email.content}
                                onChange={(value) => updateEmailInEdit(index, 'content', value)}
                                placeholder="Créez un email attractif avec du texte formaté, des images et des liens..."
                              />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Listes de destination</Label>
                    <div className="mt-2 space-y-2">
                      {lists.map(list => (
                        <div key={list.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`edit-list-${list.id}`}
                            checked={editingCampaign.list_ids.includes(list.id)}
                            onChange={() => toggleListSelection(list.id, true)}
                            className="w-4 h-4"
                          />
                          <label htmlFor={`edit-list-${list.id}`} className="text-sm text-slate-700">
                            {list.name} ({list.subscriber_count} abonnés)
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button onClick={updateCampaign} className="w-full bg-gradient-to-r from-purple-500 to-pink-600">
                    Enregistrer les modifications
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {campaigns.length === 0 ? (
            <Card className="bg-white/60 backdrop-blur border-slate-200/50">
              <CardContent className="py-12">
                <p className="text-slate-500 text-center">Aucune campagne créée</p>
              </CardContent>
            </Card>
          ) : (
            campaigns.map(campaign => (
              <Card key={campaign.id} className="bg-white/60 backdrop-blur border-slate-200/50 hover:shadow-lg transition-all duration-300" data-testid={`campaign-card-${campaign.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-slate-800">{campaign.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {campaign.emails && campaign.emails.length} email{campaign.emails && campaign.emails.length > 1 ? 's' : ''} dans cette campagne
                      </CardDescription>
                    </div>
                    <Badge 
                      variant={campaign.status === 'sent' ? 'default' : 'secondary'}
                      className={campaign.status === 'sent' ? 'bg-emerald-500' : ''}
                    >
                      {campaign.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {campaign.emails && campaign.emails.slice(0, 2).map((email, idx) => (
                      <div key={idx} className="text-sm bg-slate-50/50 p-2 rounded">
                        <p className="font-medium text-slate-800">{email.subject}</p>
                        <p className="text-slate-600 line-clamp-1">{email.content}</p>
                      </div>
                    ))}
                    {campaign.emails && campaign.emails.length > 2 && (
                      <p className="text-xs text-slate-500">+ {campaign.emails.length - 2} email(s) supplémentaire(s)</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1 text-sm text-slate-600">
                      <span>{campaign.sent_count} envoyés (dernier envoi)</span>
                      {campaign.total_sends > campaign.sent_count && (
                        <span className="text-xs text-emerald-600">Total: {campaign.total_sends} envoyés</span>
                      )}
                      <span className="text-xs">{campaign.list_ids.length} liste(s)</span>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => openEditCampaign(campaign.id)}
                        data-testid={`edit-campaign-${campaign.id}`}
                        className="hover:text-cyan-600 hover:bg-cyan-50"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                      {campaign.status === 'draft' && (
                        <Button 
                          size="sm" 
                          onClick={() => sendCampaign(campaign.id, false)}
                          data-testid={`send-campaign-${campaign.id}`}
                          className="bg-gradient-to-r from-emerald-500 to-teal-600"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Envoyer
                        </Button>
                      )}
                      {campaign.status === 'sent' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openResendModal(campaign)}
                          data-testid={`resend-campaign-${campaign.id}`}
                          className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Sélectionner & Renvoyer
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => deleteCampaign(campaign.id)}
                        data-testid={`delete-campaign-${campaign.id}`}
                        className="hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const SequencesPage = () => {
  const navigate = useNavigate();
  const [sequences, setSequences] = useState([]);
  const [lists, setLists] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [newSequenceOpen, setNewSequenceOpen] = useState(false);
  const [editSequenceOpen, setEditSequenceOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState(null);
  const [newSequence, setNewSequence] = useState({
    name: "",
    campaign_id: "",
    list_ids: [],
    email_schedule: []
  });

  useEffect(() => {
    loadSequences();
    loadLists();
    loadCampaigns();
  }, []);

  const loadSequences = async () => {
    try {
      const response = await axios.get(`${API}/sequences`);
      setSequences(response.data);
    } catch (error) {
      console.error("Error loading sequences:", error);
    }
  };

  const loadLists = async () => {
    try {
      const response = await axios.get(`${API}/lists`);
      setLists(response.data);
    } catch (error) {
      console.error("Error loading lists:", error);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error("Error loading campaigns:", error);
    }
  };

  const handleCampaignSelect = (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    setSelectedCampaign(campaign);
    setNewSequence({
      ...newSequence,
      campaign_id: campaignId,
      email_schedule: campaign ? campaign.emails.map((_, idx) => ({ email_index: idx, delay_days: idx })) : []
    });
  };

  const createSequence = async () => {
    if (!newSequence.name || !newSequence.campaign_id || newSequence.list_ids.length === 0) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    try {
      // Détecter le timezone de l'utilisateur
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Ajouter le timezone à chaque élément du schedule qui utilise specific_datetime
      const scheduleWithTimezone = newSequence.email_schedule.map(item => ({
        ...item,
        timezone: item.specific_datetime ? userTimezone : undefined
      }));
      
      await axios.post(`${API}/sequences`, {
        ...newSequence,
        email_schedule: scheduleWithTimezone
      });
      
      toast.success(`Séquence créée! (Timezone: ${userTimezone})`);
      setNewSequenceOpen(false);
      setNewSequence({ name: "", campaign_id: "", list_ids: [], email_schedule: [] });
      setSelectedCampaign(null);
      loadSequences();
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const openEditSequence = async (sequenceId) => {
    try {
      const response = await axios.get(`${API}/sequences/${sequenceId}`);
      const seq = response.data;
      setEditingSequence(seq);
      
      // Load the associated campaign
      const campaign = campaigns.find(c => c.id === seq.campaign_id);
      setSelectedCampaign(campaign);
      
      setEditSequenceOpen(true);
    } catch (error) {
      toast.error("Erreur lors du chargement de la séquence");
    }
  };

  const updateSequence = async () => {
    if (!editingSequence.name || !editingSequence.campaign_id || editingSequence.list_ids.length === 0) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    try {
      // Détecter le timezone de l'utilisateur
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Ajouter le timezone à chaque élément du schedule qui utilise specific_datetime
      const scheduleWithTimezone = editingSequence.email_schedule.map(item => ({
        ...item,
        timezone: item.specific_datetime ? userTimezone : undefined
      }));
      
      await axios.put(`${API}/sequences/${editingSequence.id}`, {
        name: editingSequence.name,
        campaign_id: editingSequence.campaign_id,
        list_ids: editingSequence.list_ids,
        email_schedule: scheduleWithTimezone
      });
      
      toast.success(`Séquence modifiée! (Timezone: ${userTimezone})`);
      setEditSequenceOpen(false);
      setEditingSequence(null);
      setSelectedCampaign(null);
      loadSequences();
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  const updateScheduleDelay = (index, delay) => {
    if (editSequenceOpen && editingSequence) {
      const updated = [...editingSequence.email_schedule];
      updated[index].delay_days = parseInt(delay) || 0;
      setEditingSequence({ ...editingSequence, email_schedule: updated });
    } else {
      const updated = [...newSequence.email_schedule];
      updated[index].delay_days = parseInt(delay) || 0;
      setNewSequence({ ...newSequence, email_schedule: updated });
    }
  };

  const updateScheduleMode = (index, mode) => {
    const isDatetime = mode === 'datetime';
    if (editSequenceOpen && editingSequence) {
      const updated = [...editingSequence.email_schedule];
      updated[index].specific_datetime = isDatetime;
      if (!isDatetime) {
        delete updated[index].specific_date;
        delete updated[index].specific_time;
      }
      setEditingSequence({ ...editingSequence, email_schedule: updated });
    } else {
      const updated = [...newSequence.email_schedule];
      updated[index].specific_datetime = isDatetime;
      if (!isDatetime) {
        delete updated[index].specific_date;
        delete updated[index].specific_time;
      }
      setNewSequence({ ...newSequence, email_schedule: updated });
    }
  };

  const updateScheduleDateTime = (index, field, value) => {
    if (editSequenceOpen && editingSequence) {
      const updated = [...editingSequence.email_schedule];
      if (field === 'date') {
        updated[index].specific_date = value;
      } else {
        updated[index].specific_time = value;
      }
      setEditingSequence({ ...editingSequence, email_schedule: updated });
    } else {
      const updated = [...newSequence.email_schedule];
      if (field === 'date') {
        updated[index].specific_date = value;
      } else {
        updated[index].specific_time = value;
      }
      setNewSequence({ ...newSequence, email_schedule: updated });
    }
  };


  const toggleEmailInSchedule = (emailIndex) => {
    if (editSequenceOpen && editingSequence) {
      const schedule = editingSequence.email_schedule;
      const exists = schedule.find(s => s.email_index === emailIndex);
      if (exists) {
        setEditingSequence({
          ...editingSequence,
          email_schedule: schedule.filter(s => s.email_index !== emailIndex)
        });
      } else {
        setEditingSequence({
          ...editingSequence,
          email_schedule: [...schedule, { email_index: emailIndex, delay_days: emailIndex }]
        });
      }
    } else {
      const schedule = newSequence.email_schedule;
      const exists = schedule.find(s => s.email_index === emailIndex);
      if (exists) {
        setNewSequence({
          ...newSequence,
          email_schedule: schedule.filter(s => s.email_index !== emailIndex)
        });
      } else {
        setNewSequence({
          ...newSequence,
          email_schedule: [...schedule, { email_index: emailIndex, delay_days: emailIndex }]
        });
      }
    }
  };

  const toggleSequence = async (sequenceId) => {
    try {
      const response = await axios.patch(`${API}/sequences/${sequenceId}/toggle`);
      toast.success(response.data.is_active ? "Séquence activée" : "Séquence désactivée");
      loadSequences();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const deleteSequence = async (sequenceId) => {
    if (!window.confirm("Supprimer cette séquence?")) return;
    try {
      await axios.delete(`${API}/sequences/${sequenceId}`);
      toast.success("Séquence supprimée");
      loadSequences();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const toggleListSelection = (listId, isEdit = false) => {
    if (isEdit && editingSequence) {
      setEditingSequence(prev => ({
        ...prev,
        list_ids: prev.list_ids.includes(listId)
          ? prev.list_ids.filter(id => id !== listId)
          : [...prev.list_ids, listId]
      }));
      return;
    }
    
    setNewSequence(prev => ({
      ...prev,
      list_ids: prev.list_ids.includes(listId)
        ? prev.list_ids.filter(id => id !== listId)
        : [...prev.list_ids, listId]
    }));
  };

  // Removed duplicate function

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Autorépondeur
              </h1>
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50">
                <BarChart3 className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <Button variant="ghost" onClick={() => navigate("/lists")} className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50">
                <List className="w-4 h-4 mr-2" />
                Listes
              </Button>
              <Button variant="ghost" onClick={() => navigate("/campaigns")} className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50">
                <Send className="w-4 h-4 mr-2" />
                Campagnes
              </Button>
              <Button variant="ghost" onClick={() => navigate("/sequences")} className="text-cyan-600 bg-cyan-50">
                <Layers className="w-4 h-4 mr-2" />
                Séquences
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-slate-800">Séquences automatiques</h2>
            <p className="text-slate-600 mt-1">Envoyez des emails programmés automatiquement</p>
          </div>
          <Dialog open={newSequenceOpen} onOpenChange={setNewSequenceOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-sequence-btn" className="bg-gradient-to-r from-orange-500 to-red-600">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle séquence
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Créer une séquence automatique</DialogTitle>
                <DialogDescription>
                  Programmez une série d'emails envoyés automatiquement
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Nom de la séquence</Label>
                  <Input
                    data-testid="sequence-name-input"
                    value={newSequence.name}
                    onChange={(e) => setNewSequence({...newSequence, name: e.target.value})}
                    placeholder="Séquence bienvenue"
                  />
                </div>
                
                <div>
                  <Label>Sélectionner une campagne</Label>
                  <select
                    className="w-full p-2 border rounded-md bg-white"
                    value={newSequence.campaign_id}
                    onChange={(e) => handleCampaignSelect(e.target.value)}
                    data-testid="campaign-select"
                  >
                    <option value="">-- Choisir une campagne --</option>
                    {campaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name} ({campaign.emails?.length || 0} emails)
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCampaign && (
                  <div>
                    <Label className="mb-2 block">Emails à inclure et leurs délais</Label>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {selectedCampaign.emails?.map((email, idx) => {
                        const scheduleItem = newSequence.email_schedule.find(s => s.email_index === idx);
                        const isSelected = !!scheduleItem;
                        
                        return (
                          <Card key={idx} className={`bg-slate-50/50 ${isSelected ? 'border-cyan-500 border-2' : ''}`}>
                            <CardContent className="pt-4 space-y-2">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-2 flex-1">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleEmailInSchedule(idx)}
                                    className="mt-1"
                                  />
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">{email.subject}</p>
                                    <p className="text-xs text-slate-600 line-clamp-1">{email.content}</p>
                                  </div>
                                </div>
                              </div>
                              {isSelected && (
                                <div className="ml-6 space-y-3 mt-2 p-3 bg-slate-50 rounded-lg">
                                  <div className="flex gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`schedule-mode-${idx}`}
                                        checked={!scheduleItem.specific_datetime}
                                        onChange={() => updateScheduleMode(idx, 'delay')}
                                        className="w-4 h-4"
                                      />
                                      <span className="text-sm text-slate-700">Délai en jours</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`schedule-mode-${idx}`}
                                        checked={scheduleItem.specific_datetime}
                                        onChange={() => updateScheduleMode(idx, 'datetime')}
                                        className="w-4 h-4"
                                      />
                                      <span className="text-sm text-slate-700">Date et heure précise</span>
                                    </label>
                                  </div>
                                  
                                  {!scheduleItem.specific_datetime ? (
                                    <div className="flex items-center gap-2">
                                      <Label className="text-xs">Délai (jours):</Label>
                                      <Input
                                        type="number"
                                        min="0"
                                        value={scheduleItem.delay_days}
                                        onChange={(e) => updateScheduleDelay(idx, e.target.value)}
                                        className="w-20 h-8"
                                      />
                                    </div>
                                  ) : (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <Label className="text-xs">Date:</Label>
                                        <Input
                                          type="date"
                                          value={scheduleItem.specific_date || ''}
                                          onChange={(e) => updateScheduleDateTime(idx, 'date', e.target.value)}
                                          className="h-8"
                                        />
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Label className="text-xs">Heure:</Label>
                                        <Input
                                          type="time"
                                          value={scheduleItem.specific_time || ''}
                                          onChange={(e) => updateScheduleDateTime(idx, 'time', e.target.value)}
                                          className="h-8"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <div>
                  <Label>Listes concernées</Label>
                  <div className="mt-2 space-y-2">
                    {lists.map(list => (
                      <div key={list.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`seq-list-${list.id}`}
                          checked={newSequence.list_ids.includes(list.id)}
                          onChange={() => toggleListSelection(list.id, false)}
                          className="w-4 h-4"
                        />
                        <label htmlFor={`seq-list-${list.id}`} className="text-sm text-slate-700">
                          {list.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <Button onClick={createSequence} data-testid="submit-sequence-btn" className="w-full bg-gradient-to-r from-orange-500 to-red-600">
                  Créer la séquence
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
          {/* Modal Modifier Séquence */}
          <Dialog open={editSequenceOpen} onOpenChange={setEditSequenceOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Modifier la séquence</DialogTitle>
                <DialogDescription>
                  Modifiez les paramètres de votre séquence automatique
                </DialogDescription>
              </DialogHeader>
              {editingSequence && (
                <div className="space-y-4">
                  <div>
                    <Label>Nom de la séquence</Label>
                    <Input
                      value={editingSequence.name}
                      onChange={(e) => setEditingSequence({...editingSequence, name: e.target.value})}
                      placeholder="Séquence bienvenue"
                    />
                  </div>
                  
                  <div>
                    <Label>Campagne liée</Label>
                    <select
                      className="w-full p-2 border rounded-md bg-white"
                      value={editingSequence.campaign_id}
                      onChange={(e) => {
                        const campaign = campaigns.find(c => c.id === e.target.value);
                        setSelectedCampaign(campaign);
                        setEditingSequence({
                          ...editingSequence,
                          campaign_id: e.target.value,
                          email_schedule: campaign ? campaign.emails.map((_, idx) => ({ email_index: idx, delay_days: idx })) : []
                        });
                      }}
                    >
                      <option value="">-- Choisir une campagne --</option>
                      {campaigns.map(campaign => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.name} ({campaign.emails?.length || 0} emails)
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedCampaign && (
                    <div>
                      <Label className="mb-2 block">Emails à inclure et leurs délais</Label>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {selectedCampaign.emails?.map((email, idx) => {
                          const scheduleItem = editingSequence.email_schedule.find(s => s.email_index === idx);
                          const isSelected = !!scheduleItem;
                          
                          return (
                            <Card key={idx} className={`bg-slate-50/50 ${isSelected ? 'border-cyan-500 border-2' : ''}`}>
                              <CardContent className="pt-4 space-y-2">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start gap-2 flex-1">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleEmailInSchedule(idx)}
                                      className="mt-1"
                                    />
                                    <div className="flex-1">
                                      <p className="font-medium text-sm">{email.subject}</p>
                                      <p className="text-xs text-slate-600 line-clamp-1">{email.content}</p>
                                    </div>
                                  </div>
                                </div>
                                {isSelected && (
                                  <div className="ml-6 space-y-3 mt-2 p-3 bg-slate-50 rounded-lg">
                                    <div className="flex gap-4">
                                      <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                          type="radio"
                                          name={`schedule-mode-edit-${idx}`}
                                          checked={!scheduleItem.specific_datetime}
                                          onChange={() => updateScheduleMode(idx, 'delay')}
                                          className="w-4 h-4"
                                        />
                                        <span className="text-sm text-slate-700">Délai en jours</span>
                                      </label>
                                      <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                          type="radio"
                                          name={`schedule-mode-edit-${idx}`}
                                          checked={scheduleItem.specific_datetime}
                                          onChange={() => updateScheduleMode(idx, 'datetime')}
                                          className="w-4 h-4"
                                        />
                                        <span className="text-sm text-slate-700">Date et heure précise</span>
                                      </label>
                                    </div>
                                    
                                    {!scheduleItem.specific_datetime ? (
                                      <div className="flex items-center gap-2">
                                        <Label className="text-xs">Délai (jours):</Label>
                                        <Input
                                          type="number"
                                          min="0"
                                          value={scheduleItem.delay_days}
                                          onChange={(e) => updateScheduleDelay(idx, e.target.value)}
                                          className="w-20 h-8"
                                        />
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                          <Label className="text-xs">Date:</Label>
                                          <Input
                                            type="date"
                                            value={scheduleItem.specific_date || ''}
                                            onChange={(e) => updateScheduleDateTime(idx, 'date', e.target.value)}
                                            className="h-8"
                                          />
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Label className="text-xs">Heure:</Label>
                                          <Input
                                            type="time"
                                            value={scheduleItem.specific_time || ''}
                                            onChange={(e) => updateScheduleDateTime(idx, 'time', e.target.value)}
                                            className="h-8"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label>Listes concernées</Label>
                    <div className="mt-2 space-y-2">
                      {lists.map(list => (
                        <div key={list.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`edit-seq-list-${list.id}`}
                            checked={editingSequence.list_ids.includes(list.id)}
                            onChange={() => toggleListSelection(list.id, true)}
                            className="w-4 h-4"
                          />
                          <label htmlFor={`edit-seq-list-${list.id}`} className="text-sm text-slate-700">
                            {list.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button onClick={updateSequence} className="w-full bg-gradient-to-r from-orange-500 to-red-600">
                    Enregistrer les modifications
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

        <div className="grid grid-cols-1 gap-4">
          {sequences.length === 0 ? (
            <Card className="bg-white/60 backdrop-blur border-slate-200/50">
              <CardContent className="py-12">
                <p className="text-slate-500 text-center">Aucune séquence créée</p>
              </CardContent>
            </Card>
          ) : (
            sequences.map(sequence => {
              const campaign = campaigns.find(c => c.id === sequence.campaign_id);
              return (
              <Card key={sequence.id} className="bg-white/60 backdrop-blur border-slate-200/50 hover:shadow-lg transition-all duration-300" data-testid={`sequence-card-${sequence.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-slate-800">{sequence.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {campaign ? campaign.name : 'Campagne supprimée'} • {sequence.email_schedule?.length || 0} emails • {sequence.list_ids.length} listes
                      </CardDescription>
                      <div className="flex gap-3 mt-2 text-sm text-slate-600">
                        <span>📨 {sequence.total_sent || 0} envoyés</span>
                        <span>👥 {sequence.active_enrollments || 0} actifs</span>
                      </div>
                    </div>
                    <Badge 
                      variant={sequence.is_active ? 'default' : 'secondary'}
                      className={sequence.is_active ? 'bg-emerald-500' : ''}
                    >
                      {sequence.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {sequence.created_at && (
                    <div className="text-xs text-slate-500 mb-3">
                      📅 Créée le {new Date(sequence.created_at).toLocaleDateString('fr-FR', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })} à {new Date(sequence.created_at).toLocaleTimeString('fr-FR', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  )}
                  <div className="space-y-2 mb-4">
                    {sequence.email_schedule?.map((scheduleItem, idx) => {
                      const email = campaign?.emails?.[scheduleItem.email_index];
                      const timeInfo = scheduleItem.specific_datetime 
                        ? `📅 ${scheduleItem.specific_date || 'Date non définie'} à ${scheduleItem.specific_time || '00:00'}`
                        : `Jour ${scheduleItem.delay_days}`;
                      return (
                        <div key={idx} className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50/50 p-2 rounded">
                          <Calendar className="w-4 h-4" />
                          <span>{timeInfo}: {email?.subject || 'Email supprimé'}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => openEditSequence(sequence.id)}
                      data-testid={`edit-sequence-${sequence.id}`}
                      className="hover:text-cyan-600 hover:bg-cyan-50"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Modifier
                    </Button>
                    <Button 
                      size="sm" 
                      variant={sequence.is_active ? "outline" : "default"}
                      onClick={() => toggleSequence(sequence.id)}
                      data-testid={`toggle-sequence-${sequence.id}`}
                      className={sequence.is_active ? "" : "bg-emerald-500 hover:bg-emerald-600"}
                    >
                      {sequence.is_active ? (
                        <><Pause className="w-4 h-4 mr-2" />Désactiver</>
                      ) : (
                        <><Play className="w-4 h-4 mr-2" />Activer</>
                      )}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => deleteSequence(sequence.id)}
                      data-testid={`delete-sequence-${sequence.id}`}
                      className="hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
            })
          )}
        </div>
      </div>
    </div>
  );
};



const NewslettersPage = () => {
  const navigate = useNavigate();
  const [newsletters, setNewsletters] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [lists, setLists] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [newsletter, setNewsletter] = useState({
    subject: "",
    content: "",
    list_ids: []
  });
  const [sending, setSending] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  const [activeTab, setActiveTab] = useState('sent'); // 'sent' or 'drafts'

  useEffect(() => {
    loadNewsletters();
    loadDrafts();
    loadLists();
  }, []);

  useEffect(() => {
    // Calculer le nombre de destinataires
    if (newsletter.list_ids.length > 0) {
      calculateRecipients();
    } else {
      setRecipientCount(0);
    }
  }, [newsletter.list_ids]);

  const loadNewsletters = async () => {
    try {
      const response = await axios.get(`${API}/newsletters`);
      // Filtrer uniquement les newsletters envoyées (non brouillons)
      setNewsletters(response.data.filter(nl => !nl.is_draft));
    } catch (error) {
      console.error("Error loading newsletters:", error);
    }
  };

  const loadDrafts = async () => {
    try {
      const response = await axios.get(`${API}/newsletters/drafts`);
      setDrafts(response.data);
    } catch (error) {
      console.error("Error loading drafts:", error);
    }
  };

  const loadLists = async () => {
    try {
      const response = await axios.get(`${API}/lists`);
      setLists(response.data);
    } catch (error) {
      console.error("Error loading lists:", error);
    }
  };

  const calculateRecipients = async () => {
    try {
      const response = await axios.get(`${API}/subscribers`);
      const allSubscribers = response.data;
      
      // Compter les abonnés vérifiés et actifs dans les listes sélectionnées
      const count = allSubscribers.filter(sub => 
        sub.verified && 
        sub.is_active && 
        sub.list_ids.some(listId => newsletter.list_ids.includes(listId))
      ).length;
      
      setRecipientCount(count);
    } catch (error) {
      console.error("Error calculating recipients:", error);
      setRecipientCount(0);
    }
  };

  const toggleListSelection = (listId) => {
    setNewsletter(prev => ({
      ...prev,
      list_ids: prev.list_ids.includes(listId)
        ? prev.list_ids.filter(id => id !== listId)
        : [...prev.list_ids, listId]
    }));
  };

  const sendNewsletter = async () => {
    if (!newsletter.subject || !newsletter.content) {
      toast.error("Sujet et contenu requis");
      return;
    }

    if (newsletter.list_ids.length === 0) {
      toast.error("Sélectionnez au moins une liste");
      return;
    }

    if (!window.confirm(`Envoyer cette newsletter à ${recipientCount} abonné(s) ?`)) {
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/newsletters/send`, newsletter);
      toast.success(`Newsletter envoyée à ${response.data.sent} abonné(s)`);
      
      // Si c'était un brouillon, le supprimer
      if (editingDraft) {
        await axios.delete(`${API}/newsletters/${editingDraft.id}`);
      }
      
      setShowForm(false);
      setEditingDraft(null);
      setNewsletter({ subject: "", content: "", list_ids: [] });
      loadNewsletters();
      loadDrafts();
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const saveDraft = async () => {
    if (!newsletter.subject && !newsletter.content) {
      toast.error("Ajoutez au moins un sujet ou contenu");
      return;
    }

    try {
      if (editingDraft) {
        // Mettre à jour le brouillon existant
        await axios.put(`${API}/newsletters/${editingDraft.id}`, newsletter);
        toast.success("Brouillon mis à jour");
      } else {
        // Créer un nouveau brouillon
        await axios.post(`${API}/newsletters/draft`, newsletter);
        toast.success("Brouillon enregistré");
      }
      
      setShowForm(false);
      setEditingDraft(null);
      setNewsletter({ subject: "", content: "", list_ids: [] });
      loadDrafts();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const editDraft = (draft) => {
    setEditingDraft(draft);
    setNewsletter({
      subject: draft.subject,
      content: draft.content,
      list_ids: draft.list_ids
    });
    setShowForm(true);
  };

  const deleteNewsletter = async (id) => {
    if (!window.confirm("Supprimer cette newsletter ?")) {
      return;
    }

    try {
      await axios.delete(`${API}/newsletters/${id}`);
      toast.success("Newsletter supprimée");
      loadNewsletters();
      loadDrafts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Newsletter
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-600 hover:text-cyan-600 hover:bg-cyan-50">
                <Home className="w-4 h-4 mr-2" />
                Accueil
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {!showForm ? (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-800">Newsletters</h2>
              <Button 
                onClick={() => {
                  setEditingDraft(null);
                  setNewsletter({ subject: "", content: "", list_ids: [] });
                  setShowForm(true);
                }}
                className="bg-gradient-to-r from-cyan-500 to-blue-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Newsletter
              </Button>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList>
                <TabsTrigger value="sent">Envoyées ({newsletters.length})</TabsTrigger>
                <TabsTrigger value="drafts">Brouillons ({drafts.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="sent" className="space-y-4 mt-4">
                {newsletters.length === 0 ? (
                  <Card className="bg-white/80 backdrop-blur-sm">
                    <CardContent className="pt-6 text-center text-slate-500">
                      Aucune newsletter envoyée pour le moment
                    </CardContent>
                  </Card>
                ) : (
                  newsletters.map(nl => (
                    <Card key={nl.id} className="bg-white/80 backdrop-blur-sm hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <CardTitle className="text-lg">{nl.subject}</CardTitle>
                            <CardDescription>
                              Envoyée le {new Date(nl.sent_at).toLocaleDateString('fr-FR', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </CardDescription>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteNewsletter(nl.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="text-sm text-slate-600 mb-3 line-clamp-2">{nl.content}</div>
                        <div className="flex gap-4 text-sm">
                          <span className="text-green-600">✅ {nl.sent_count} envoyés</span>
                          {nl.failed_count > 0 && (
                            <span className="text-red-600">❌ {nl.failed_count} échecs</span>
                          )}
                          <span className="text-slate-500">📊 {nl.recipient_count} destinataires</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="drafts" className="space-y-4 mt-4">
                {drafts.length === 0 ? (
                  <Card className="bg-white/80 backdrop-blur-sm">
                    <CardContent className="pt-6 text-center text-slate-500">
                      Aucun brouillon enregistré
                    </CardContent>
                  </Card>
                ) : (
                  drafts.map(draft => (
                    <Card key={draft.id} className="bg-white/80 backdrop-blur-sm hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <CardTitle className="text-lg">
                              {draft.subject || <span className="text-slate-400 italic">Sans titre</span>}
                            </CardTitle>
                            <CardDescription>
                              Modifié le {new Date(draft.updated_at).toLocaleDateString('fr-FR', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </CardDescription>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => editDraft(draft)}
                              className="text-cyan-600 hover:text-cyan-700"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Modifier
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteNewsletter(draft.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="text-sm text-slate-600 line-clamp-2">
                          {draft.content || <span className="text-slate-400 italic">Aucun contenu</span>}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <Card className="bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle>{editingDraft ? "Modifier le brouillon" : "Nouvelle Newsletter"}</CardTitle>
              <CardDescription>
                {editingDraft ? "Modifiez et envoyez votre brouillon" : "Envoyez un email manuel à vos abonnés"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Sujet</Label>
                <Input
                  value={newsletter.subject}
                  onChange={(e) => setNewsletter({...newsletter, subject: e.target.value})}
                  placeholder="Objet de votre newsletter"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Contenu 📝✨</Label>
                <RichTextEditor
                  value={newsletter.content}
                  onChange={(value) => setNewsletter({...newsletter, content: value})}
                  placeholder="Rédigez une newsletter attractive avec du texte formaté, des emojis 😊, des images 📷 et des liens 🔗..."
                />
              </div>

              <div>
                <Label>Listes destinataires</Label>
                <div className="mt-2 space-y-2">
                  {lists.map(list => (
                    <div key={list.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`nl-list-${list.id}`}
                        checked={newsletter.list_ids.includes(list.id)}
                        onChange={() => toggleListSelection(list.id)}
                        className="w-4 h-4"
                      />
                      <label htmlFor={`nl-list-${list.id}`} className="text-sm text-slate-700">
                        {list.name} ({list.subscriber_count || 0} abonnés)
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {recipientCount > 0 && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                  <p className="text-cyan-800 font-medium">
                    📧 Cette newsletter sera envoyée à <span className="text-cyan-600 font-bold">{recipientCount}</span> abonné(s) vérifié(s)
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingDraft(null);
                    setNewsletter({ subject: "", content: "", list_ids: [] });
                  }}
                  disabled={sending}
                >
                  Annuler
                </Button>
                <Button
                  variant="outline"
                  onClick={saveDraft}
                  disabled={sending}
                  className="border-orange-500 text-orange-600 hover:bg-orange-50"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {editingDraft ? "Mettre à jour le brouillon" : "Enregistrer comme brouillon"}
                </Button>
                <Button
                  onClick={sendNewsletter}
                  disabled={sending || recipientCount === 0}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600"
                >
                  {sending ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer à {recipientCount} abonné(s)
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const VerifyPage = () => {
  const { token } = useParams();
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      await axios.get(`${API}/verify/${token}`);
      setVerified(true);
    } catch (error) {
      setVerified(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Vérification en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center">
      <Card className="w-full max-w-md bg-white/60 backdrop-blur">
        <CardContent className="pt-6 text-center">
          {verified ? (
            <>
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Email vérifié!</h2>
              <p className="text-slate-600">Votre inscription est maintenant confirmée.</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✕</span>
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Erreur</h2>
              <p className="text-slate-600">Lien de vérification invalide ou expiré.</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const UnsubscribePage = () => {
  const { token } = useParams();
  const [unsubscribed, setUnsubscribed] = useState(false);

  const handleUnsubscribe = async () => {
    try {
      await axios.post(`${API}/unsubscribe/${token}`);
      setUnsubscribed(true);
      toast.success("Vous êtes désinscrit");
    } catch (error) {
      toast.error("Erreur");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center">
      <Card className="w-full max-w-md bg-white/60 backdrop-blur">
        <CardHeader>
          <CardTitle>Se désinscrire</CardTitle>
          <CardDescription>
            {unsubscribed ? "Vous êtes maintenant désinscrit" : "Confirmer la désinscription"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!unsubscribed ? (
            <Button onClick={handleUnsubscribe} className="w-full bg-red-500 hover:bg-red-600">
              Confirmer la désinscription
            </Button>
          ) : (
            <p className="text-center text-slate-600">Vous ne recevrez plus d'emails de notre part.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Formulaire d'inscription public
const SubscribePage = () => {
  const { listId } = useParams();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ email: "", first_name: "", last_name: "" });
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadList();
  }, [listId]);

  const loadList = async () => {
    try {
      const response = await axios.get(`${API}/lists/${listId}`);
      setList(response.data);
    } catch (error) {
      console.error("Error loading list:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/subscribe/public`, {
        ...formData,
        list_id: listId
      });
      
      setSubmitted(true);
      if (response.data.message === "already_subscribed") {
        setMessage("Vous êtes déjà inscrit à cette liste!");
      } else if (response.data.message === "subscribed") {
        setMessage("Vous avez été ajouté à cette liste!");
      } else {
        setMessage("Vérifiez votre email pour confirmer votre inscription!");
      }
    } catch (error) {
      setMessage("Une erreur est survenue. Veuillez réessayer.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center">
        <Card className="w-full max-w-md bg-white/60 backdrop-blur">
          <CardContent className="pt-6 text-center">
            <p className="text-slate-600">Liste introuvable</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/60 backdrop-blur">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Merci!</h2>
            <p className="text-slate-600">{message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/60 backdrop-blur">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Inscription à {list.name}</CardTitle>
          <CardDescription>{list.description}</CardDescription>
          <p className="text-sm text-slate-600 mt-2">{list.subscriber_count} personnes déjà inscrites</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="votre@email.com"
                data-testid="subscribe-email-input"
              />
            </div>
            <div>
              <Label>Prénom</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                placeholder="Jean"
              />
            </div>
            <div>
              <Label>Nom</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                placeholder="Dupont"
              />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-cyan-500 to-blue-600" data-testid="subscribe-submit-btn">
              S'inscrire
            </Button>
            <p className="text-xs text-slate-500 text-center">
              En vous inscrivant, vous acceptez de recevoir des emails. Vous pourrez vous désinscrire à tout moment.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/lists" element={<ListsPage />} />
          <Route path="/lists/:listId" element={<ListDetailPage />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/sequences" element={<SequencesPage />} />
          <Route path="/newsletters" element={<NewslettersPage />} />
          <Route path="/subscribe/:listId" element={<SubscribePage />} />
          <Route path="/verify/:token" element={<VerifyPage />} />
          <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;